/**
 * PhotoRealMinistryIcons
 *
 * Skeuomorphic 64×64 SVG illustrations for the Ministry module.
 * All icons reference shared gradients from SharedSvgDefs (pr-* IDs).
 * Mount SharedSvgDefs once at app root before using these.
 *
 * Module accent: violet #6d3fa0
 */

import { forwardRef, type SVGProps } from "react";

type IconProps = SVGProps<SVGSVGElement>;

// ═══════════════════════════════════════════════════════════════
// NAVIGATION — FloatingNav group icons + items
// ═══════════════════════════════════════════════════════════════

/** Warm house with violet roof — General / Home group icon */
export const PRMinistryHome = forwardRef<SVGSVGElement, IconProps>(
  (props, ref) => (
    <svg ref={ref} viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg" {...props}>
      <ellipse cx="32" cy="56" rx="22" ry="4" fill="#000" opacity={0.06} />
      {/* House body */}
      <rect x="14" y="30" width="36" height="22" rx="2" fill="url(#pr-paper-white)" />
      <rect x="14" y="30" width="36" height="22" rx="2" stroke="#a78bfa" strokeWidth="1" opacity={0.4} />
      {/* Roof */}
      <path d="M10 32l22-18 22 18" fill="url(#pr-violet)" />
      <path d="M10 32l22-18 22 18" fill="url(#pr-glass-shine)" opacity={0.4} />
      <path d="M10 32l22-18 22 18" stroke="#5b21b6" strokeWidth="1.5" strokeLinejoin="round" opacity={0.5} />
      {/* Door */}
      <rect x="27" y="38" width="10" height="14" rx="2" fill="url(#pr-warm-wood)" />
      <circle cx="35" cy="45" r="1" fill="url(#pr-gold-face)" />
      {/* Window left */}
      <rect x="17" y="35" width="7" height="7" rx="1" fill="url(#pr-stained-cool)" opacity={0.7} />
      <path d="M20.5 35v7M17 38.5h7" stroke="#fff" strokeWidth="0.8" opacity={0.5} />
      {/* Window right */}
      <rect x="40" y="35" width="7" height="7" rx="1" fill="url(#pr-stained-cool)" opacity={0.7} />
      <path d="M43.5 35v7M40 38.5h7" stroke="#fff" strokeWidth="0.8" opacity={0.5} />
      {/* Cross on roof peak */}
      <path d="M32 10v8M29 13h6" stroke="#fde047" strokeWidth="2" strokeLinecap="round" />
    </svg>
  ),
);
PRMinistryHome.displayName = "PRMinistryHome";

/** Clipboard with bar chart + violet trend arrow — Ministry Overview */
export const PRMinistryOverview = forwardRef<SVGSVGElement, IconProps>(
  (props, ref) => (
    <svg ref={ref} viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg" {...props}>
      <ellipse cx="32" cy="56" rx="20" ry="4" fill="#000" opacity={0.06} />
      {/* Clipboard body */}
      <rect x="10" y="10" width="44" height="42" rx="4" fill="url(#pr-paper-white)" />
      <rect x="10" y="10" width="44" height="42" rx="4" stroke="#c4b5fd" strokeWidth="1" opacity={0.5} />
      {/* Clip */}
      <rect x="24" y="6" width="16" height="8" rx="3" fill="url(#pr-metal)" />
      <rect x="28" y="4" width="8" height="6" rx="2" fill="url(#pr-violet)" />
      {/* Bar chart */}
      <rect x="16" y="34" width="8" height="12" rx="2" fill="url(#pr-violet-light)" />
      <rect x="28" y="28" width="8" height="18" rx="2" fill="url(#pr-violet)" opacity={0.7} />
      <rect x="40" y="22" width="8" height="24" rx="2" fill="url(#pr-violet-deep)" />
      {/* Trend arrow */}
      <path d="M18 32l12-8 10-4 8-6" stroke="#7c3aed" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M44 12l4 2-2 4" stroke="#7c3aed" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  ),
);
PRMinistryOverview.displayName = "PRMinistryOverview";

/** Open book with violet ribbon — Ministry group icon */
export const PRMinistryGroup = forwardRef<SVGSVGElement, IconProps>(
  (props, ref) => (
    <svg ref={ref} viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg" {...props}>
      <ellipse cx="32" cy="56" rx="24" ry="4" fill="#000" opacity={0.06} />
      {/* Left page */}
      <path d="M6 12c0-2 1-3 3-3h20v38H9c-2 0-3-1-3-3V12z" fill="url(#pr-paper-white)" />
      <path d="M6 12c0-2 1-3 3-3h20v38H9c-2 0-3-1-3-3V12z" stroke="#d1d5db" strokeWidth="1" />
      {/* Right page */}
      <path d="M58 12c0-2-1-3-3-3H35v38h20c2 0 3-1 3-3V12z" fill="url(#pr-paper-white)" />
      <path d="M58 12c0-2-1-3-3-3H35v38h20c2 0 3-1 3-3V12z" stroke="#d1d5db" strokeWidth="1" />
      {/* Spine */}
      <rect x="29" y="8" width="6" height="40" fill="url(#pr-violet-deep)" />
      <rect x="29" y="8" width="6" height="40" fill="url(#pr-glass-shine)" opacity={0.4} />
      {/* Text lines left */}
      <path d="M10 18h16M10 24h14M10 30h16M10 36h12" stroke="#c4b5fd" strokeWidth="1.5" strokeLinecap="round" opacity={0.5} />
      {/* Text lines right */}
      <path d="M38 18h16M38 24h14M38 30h16M38 36h12" stroke="#c4b5fd" strokeWidth="1.5" strokeLinecap="round" opacity={0.5} />
      {/* Ribbon bookmark */}
      <path d="M42 8v16l3-3 3 3V8" fill="url(#pr-violet)" opacity={0.8} />
    </svg>
  ),
);
PRMinistryGroup.displayName = "PRMinistryGroup";

/** Tracking category — clipboard with checkmarks */
export const PRMinistryTracking = forwardRef<SVGSVGElement, IconProps>(
  (props, ref) => (
    <svg ref={ref} viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg" {...props}>
      <ellipse cx="32" cy="56" rx="20" ry="4" fill="#000" opacity={0.06} />
      {/* Clipboard */}
      <rect x="12" y="10" width="40" height="42" rx="4" fill="url(#pr-paper-white)" />
      <rect x="12" y="10" width="40" height="42" rx="4" stroke="#c4b5fd" strokeWidth="1" opacity={0.4} />
      {/* Clip */}
      <rect x="24" y="6" width="16" height="8" rx="3" fill="url(#pr-metal)" />
      <rect x="28" y="4" width="8" height="6" rx="2" fill="url(#pr-violet)" />
      {/* Checklist rows */}
      {/* Row 1 - checked */}
      <rect x="18" y="20" width="8" height="8" rx="2" fill="url(#pr-felt-green)" />
      <path d="M20 24l2 2 4-4" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      <rect x="30" y="22" width="16" height="4" rx="1" fill="#c4b5fd" opacity={0.5} />
      {/* Row 2 - checked */}
      <rect x="18" y="32" width="8" height="8" rx="2" fill="url(#pr-felt-green)" />
      <path d="M20 36l2 2 4-4" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      <rect x="30" y="34" width="14" height="4" rx="1" fill="#c4b5fd" opacity={0.5} />
      {/* Row 3 - unchecked */}
      <rect x="18" y="44" width="8" height="8" rx="2" fill="#f1f5f9" stroke="#c4b5fd" strokeWidth="1" />
      <rect x="30" y="46" width="12" height="4" rx="1" fill="#c4b5fd" opacity={0.3} />
    </svg>
  ),
);
PRMinistryTracking.displayName = "PRMinistryTracking";

// ═══════════════════════════════════════════════════════════════
// SERVICES & WORSHIP
// ═══════════════════════════════════════════════════════════════

/** Church with stained glass window + cross */
export const PRServices = forwardRef<SVGSVGElement, IconProps>(
  (props, ref) => (
    <svg ref={ref} viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg" {...props}>
      <ellipse cx="32" cy="56" rx="22" ry="4" fill="#000" opacity={0.06} />
      {/* Church body */}
      <rect x="12" y="28" width="40" height="24" rx="2" fill="url(#pr-paper-white)" />
      <rect x="12" y="28" width="40" height="24" rx="2" stroke="#a78bfa" strokeWidth="1" opacity={0.3} />
      {/* Steeple */}
      <path d="M22 28l10-16 10 16" fill="url(#pr-violet)" />
      <path d="M22 28l10-16 10 16" fill="url(#pr-glass-shine)" opacity={0.4} />
      {/* Cross */}
      <path d="M32 6v10M29 10h6" stroke="#fde047" strokeWidth="2.5" strokeLinecap="round" />
      {/* Rose window (stained glass circle) */}
      <circle cx="32" cy="34" r="6" fill="url(#pr-stained-warm)" />
      <circle cx="32" cy="34" r="6" fill="url(#pr-glass-shine)" opacity={0.5} />
      <circle cx="32" cy="34" r="6" stroke="#7c3aed" strokeWidth="1" opacity={0.5} />
      {/* Petal dividers */}
      <path d="M32 28v12M26 34h12" stroke="#fff" strokeWidth="0.8" opacity={0.4} />
      {/* Double doors */}
      <rect x="27" y="42" width="4" height="10" rx="1" fill="url(#pr-warm-wood)" />
      <rect x="33" y="42" width="4" height="10" rx="1" fill="url(#pr-warm-wood)" />
      {/* Bell tower windows */}
      <rect x="15" y="32" width="4" height="6" rx="1" fill="url(#pr-stained-cool)" opacity={0.5} />
      <rect x="45" y="32" width="4" height="6" rx="1" fill="url(#pr-stained-cool)" opacity={0.5} />
    </svg>
  ),
);
PRServices.displayName = "PRServices";

/** Music note with vinyl record — Song Library */
export const PRSongLibrary = forwardRef<SVGSVGElement, IconProps>(
  (props, ref) => (
    <svg ref={ref} viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg" {...props}>
      <ellipse cx="32" cy="56" rx="20" ry="4" fill="#000" opacity={0.06} />
      {/* Record disc */}
      <circle cx="30" cy="34" r="20" fill="#1e1b4b" />
      <circle cx="30" cy="34" r="20" stroke="#4c1d95" strokeWidth="1" />
      {/* Grooves */}
      <circle cx="30" cy="34" r="16" fill="none" stroke="#312e81" strokeWidth="0.5" />
      <circle cx="30" cy="34" r="12" fill="none" stroke="#312e81" strokeWidth="0.5" />
      <circle cx="30" cy="34" r="8" fill="none" stroke="#312e81" strokeWidth="0.5" />
      {/* Label */}
      <circle cx="30" cy="34" r="6" fill="url(#pr-violet)" />
      <circle cx="30" cy="34" r="6" fill="url(#pr-glass-shine)" />
      <circle cx="30" cy="34" r="1.5" fill="#1e1b4b" />
      {/* Shine highlight */}
      <path d="M16 22a20 20 0 0128 0" fill="none" stroke="#fff" strokeWidth="1" opacity={0.15} />
      {/* Music notes floating */}
      <path d="M46 10v14" stroke="url(#pr-violet-deep)" strokeWidth="2.5" strokeLinecap="round" />
      <circle cx="44" cy="24" r="3.5" fill="url(#pr-violet)" />
      <circle cx="44" cy="24" r="3.5" fill="url(#pr-glass-shine)" />
      {/* Small note */}
      <path d="M54 8v10" stroke="#a78bfa" strokeWidth="2" strokeLinecap="round" />
      <circle cx="52" cy="18" r="2.5" fill="#a78bfa" opacity={0.7} />
      {/* Sparkle */}
      <circle cx="56" cy="6" r="1" fill="#fde047" />
      <path d="M56 4v4M54 6h4" stroke="#fde047" strokeWidth="0.8" strokeLinecap="round" opacity={0.6} />
    </svg>
  ),
);
PRSongLibrary.displayName = "PRSongLibrary";

// ═══════════════════════════════════════════════════════════════
// VOLUNTEERS
// ═══════════════════════════════════════════════════════════════

/** Raised hands with heart — Volunteers overview */
export const PRVolunteers = forwardRef<SVGSVGElement, IconProps>(
  (props, ref) => (
    <svg ref={ref} viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg" {...props}>
      <ellipse cx="32" cy="56" rx="22" ry="4" fill="#000" opacity={0.06} />
      {/* Left person */}
      <circle cx="18" cy="20" r="7" fill="url(#pr-violet-light)" />
      <circle cx="18" cy="20" r="7" stroke="#a78bfa" strokeWidth="1" opacity={0.5} />
      <path d="M6 48c0-6 5-10 12-10s12 4 12 10" fill="url(#pr-violet-light)" />
      <path d="M6 48c0-6 5-10 12-10s12 4 12 10" stroke="#a78bfa" strokeWidth="1" opacity={0.4} />
      {/* Right person */}
      <circle cx="46" cy="20" r="7" fill="url(#pr-violet-light)" />
      <circle cx="46" cy="20" r="7" stroke="#a78bfa" strokeWidth="1" opacity={0.5} />
      <path d="M34 48c0-6 5-10 12-10s12 4 12 10" fill="url(#pr-violet-light)" />
      <path d="M34 48c0-6 5-10 12-10s12 4 12 10" stroke="#a78bfa" strokeWidth="1" opacity={0.4} />
      {/* Connecting heart in center */}
      <path d="M32 28s-4-5-8-2c-5 4 0 10 8 16 8-6 13-12 8-16-4-3-8 2-8 2z" fill="url(#pr-violet)" />
      <path d="M32 28s-4-5-8-2c-5 4 0 10 8 16 8-6 13-12 8-16-4-3-8 2-8 2z" fill="url(#pr-glass-shine)" opacity={0.3} />
    </svg>
  ),
);
PRVolunteers.displayName = "PRVolunteers";

/** Shield with green check — Background Checks */
export const PRBackgroundChecks = forwardRef<SVGSVGElement, IconProps>(
  (props, ref) => (
    <svg ref={ref} viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg" {...props}>
      <ellipse cx="32" cy="56" rx="18" ry="3.5" fill="#000" opacity={0.06} />
      {/* Shield */}
      <path d="M32 6L10 16v16c0 14 10 22 22 26 12-4 22-12 22-26V16L32 6z" fill="url(#pr-violet)" />
      <path d="M32 6L10 16v16c0 14 10 22 22 26 12-4 22-12 22-26V16L32 6z" fill="url(#pr-glass-shine)" opacity={0.3} />
      <path d="M32 6L10 16v16c0 14 10 22 22 26 12-4 22-12 22-26V16L32 6z" stroke="#5b21b6" strokeWidth="1.5" opacity={0.4} />
      {/* Inner shield highlight */}
      <path d="M32 12L16 20v12c0 10 7 17 16 20 9-3 16-10 16-20V20L32 12z" fill="#fff" opacity={0.1} />
      {/* Green check circle */}
      <circle cx="32" cy="30" r="12" fill="url(#pr-felt-green)" />
      <circle cx="32" cy="30" r="12" fill="url(#pr-glass-shine)" />
      <path d="M24 30l5 5 10-10" stroke="#fff" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  ),
);
PRBackgroundChecks.displayName = "PRBackgroundChecks";

/** Gold medal with ribbon — Certifications */
export const PRCertifications = forwardRef<SVGSVGElement, IconProps>(
  (props, ref) => (
    <svg ref={ref} viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg" {...props}>
      <ellipse cx="32" cy="56" rx="16" ry="3" fill="#000" opacity={0.06} />
      {/* Ribbons */}
      <path d="M22 38l-8 18 10-4 4 6 2-16" fill="url(#pr-violet)" opacity={0.8} />
      <path d="M42 38l8 18-10-4-4 6-2-16" fill="url(#pr-violet-deep)" opacity={0.8} />
      {/* Medal body */}
      <circle cx="32" cy="28" r="18" fill="url(#pr-gold-face)" />
      <circle cx="32" cy="28" r="18" fill="url(#pr-gold-shine)" />
      <circle cx="32" cy="28" r="18" stroke="url(#pr-gold-rim)" strokeWidth="2" />
      {/* Inner ring */}
      <circle cx="32" cy="28" r="13" fill="none" stroke="#ca8a04" strokeWidth="1.5" opacity={0.5} />
      {/* Star emblem */}
      <path d="M32 18l3 6.5 7 1-5 5 1.2 7L32 34l-6.2 3.5L27 30.5l-5-5 7-1z" fill="#fef08a" stroke="#ca8a04" strokeWidth="0.8" opacity={0.8} />
    </svg>
  ),
);
PRCertifications.displayName = "PRCertifications";

/** Clipboard with welcome badge — Volunteer Onboarding */
export const PRVolunteerOnboarding = forwardRef<SVGSVGElement, IconProps>(
  (props, ref) => (
    <svg ref={ref} viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg" {...props}>
      <ellipse cx="32" cy="56" rx="20" ry="4" fill="#000" opacity={0.06} />
      {/* Clipboard */}
      <rect x="12" y="10" width="40" height="42" rx="4" fill="url(#pr-paper-white)" />
      <rect x="12" y="10" width="40" height="42" rx="4" stroke="#c4b5fd" strokeWidth="1" opacity={0.4} />
      {/* Clip */}
      <rect x="24" y="6" width="16" height="8" rx="3" fill="url(#pr-metal)" />
      <rect x="28" y="4" width="8" height="6" rx="2" fill="url(#pr-violet)" />
      {/* Checklist items */}
      <rect x="18" y="22" width="6" height="6" rx="1.5" fill="url(#pr-felt-green)" />
      <path d="M19.5 25l1.5 1.5 3-3" stroke="#fff" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
      <rect x="28" y="23" width="18" height="4" rx="1" fill="#c4b5fd" opacity={0.4} />
      <rect x="18" y="32" width="6" height="6" rx="1.5" fill="url(#pr-felt-green)" />
      <path d="M19.5 35l1.5 1.5 3-3" stroke="#fff" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
      <rect x="28" y="33" width="14" height="4" rx="1" fill="#c4b5fd" opacity={0.4} />
      <rect x="18" y="42" width="6" height="6" rx="1.5" fill="#f1f5f9" stroke="#c4b5fd" strokeWidth="0.8" />
      <rect x="28" y="43" width="16" height="4" rx="1" fill="#c4b5fd" opacity={0.3} />
      {/* Green plus badge */}
      <circle cx="48" cy="14" r="8" fill="url(#pr-felt-green)" />
      <circle cx="48" cy="14" r="8" fill="url(#pr-glass-shine)" />
      <path d="M48 10v8M44 14h8" stroke="#fff" strokeWidth="2.5" strokeLinecap="round" />
    </svg>
  ),
);
PRVolunteerOnboarding.displayName = "PRVolunteerOnboarding";

/** Clock with violet accent — Service Hours */
export const PRServiceHours = forwardRef<SVGSVGElement, IconProps>(
  (props, ref) => (
    <svg ref={ref} viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg" {...props}>
      <ellipse cx="32" cy="56" rx="20" ry="4" fill="#000" opacity={0.06} />
      {/* Clock body */}
      <circle cx="32" cy="30" r="22" fill="url(#pr-paper-white)" />
      <circle cx="32" cy="30" r="22" stroke="url(#pr-violet)" strokeWidth="3" />
      <circle cx="32" cy="30" r="22" fill="url(#pr-glass-shine)" opacity={0.3} />
      {/* Hour markers */}
      <circle cx="32" cy="12" r="2" fill="#7c3aed" opacity={0.6} />
      <circle cx="50" cy="30" r="2" fill="#7c3aed" opacity={0.6} />
      <circle cx="32" cy="48" r="2" fill="#7c3aed" opacity={0.6} />
      <circle cx="14" cy="30" r="2" fill="#7c3aed" opacity={0.6} />
      {/* Hour hand */}
      <path d="M32 30l-6-10" stroke="#5b21b6" strokeWidth="3" strokeLinecap="round" />
      {/* Minute hand */}
      <path d="M32 30l10-4" stroke="#7c3aed" strokeWidth="2.5" strokeLinecap="round" />
      {/* Center dot */}
      <circle cx="32" cy="30" r="3" fill="url(#pr-violet-deep)" />
      <circle cx="32" cy="30" r="3" fill="url(#pr-glass-shine)" />
      {/* Heart badge */}
      <circle cx="50" cy="12" r="7" fill="url(#pr-violet)" />
      <circle cx="50" cy="12" r="7" fill="url(#pr-glass-shine)" />
      <path d="M50 9.5s-1.5-1.2-2.8-.2c-1.5 1.2 0 3 2.8 5 2.8-2 4.3-3.8 2.8-5-1.3-1-2.8.2-2.8.2z" fill="#fff" opacity={0.8} />
    </svg>
  ),
);
PRServiceHours.displayName = "PRServiceHours";

/** Calendar with checkmark — My Schedule / Attendance */
export const PRAttendance = forwardRef<SVGSVGElement, IconProps>(
  (props, ref) => (
    <svg ref={ref} viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg" {...props}>
      <ellipse cx="32" cy="56" rx="22" ry="4" fill="#000" opacity={0.06} />
      {/* Calendar body */}
      <rect x="8" y="14" width="48" height="38" rx="4" fill="url(#pr-paper-white)" />
      <rect x="8" y="14" width="48" height="38" rx="4" stroke="#c4b5fd" strokeWidth="1" opacity={0.4} />
      {/* Header band */}
      <rect x="8" y="14" width="48" height="12" rx="4" fill="url(#pr-violet)" />
      <rect x="8" y="22" width="48" height="4" fill="url(#pr-violet)" />
      <rect x="8" y="14" width="48" height="12" fill="url(#pr-glass-shine)" opacity={0.3} />
      {/* Binding rings */}
      <rect x="20" y="10" width="4" height="10" rx="2" fill="url(#pr-metal)" />
      <rect x="40" y="10" width="4" height="10" rx="2" fill="url(#pr-metal)" />
      {/* Date grid */}
      <circle cx="18" cy="34" r="3" fill="#c4b5fd" opacity={0.3} />
      <circle cx="28" cy="34" r="3" fill="#c4b5fd" opacity={0.3} />
      <circle cx="38" cy="34" r="3" fill="#c4b5fd" opacity={0.3} />
      <circle cx="48" cy="34" r="3" fill="#c4b5fd" opacity={0.3} />
      <circle cx="18" cy="44" r="3" fill="#c4b5fd" opacity={0.3} />
      <circle cx="28" cy="44" r="3" fill="#c4b5fd" opacity={0.3} />
      {/* Active date with check */}
      <circle cx="38" cy="44" r="5" fill="url(#pr-felt-green)" />
      <path d="M35 44l2 2 4-4" stroke="#fff" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  ),
);
PRAttendance.displayName = "PRAttendance";

/** Baby face with building blocks — Kids Check-In */
export const PRKidsCheckin = forwardRef<SVGSVGElement, IconProps>(
  (props, ref) => (
    <svg ref={ref} viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg" {...props}>
      <ellipse cx="32" cy="56" rx="20" ry="4" fill="#000" opacity={0.06} />
      {/* Baby face */}
      <circle cx="28" cy="26" r="16" fill="url(#pr-violet-light)" />
      <circle cx="28" cy="26" r="16" stroke="#a78bfa" strokeWidth="1.5" opacity={0.5} />
      <circle cx="28" cy="26" r="16" fill="url(#pr-glass-shine)" opacity={0.3} />
      {/* Eyes */}
      <circle cx="22" cy="24" r="2.5" fill="#4c1d95" />
      <circle cx="34" cy="24" r="2.5" fill="#4c1d95" />
      <circle cx="23" cy="23" r="1" fill="#fff" />
      <circle cx="35" cy="23" r="1" fill="#fff" />
      {/* Smile */}
      <path d="M22 30c2 3 8 3 12 0" stroke="#7c3aed" strokeWidth="1.5" strokeLinecap="round" fill="none" />
      {/* Cheeks */}
      <circle cx="18" cy="28" r="3" fill="#f9a8d4" opacity={0.3} />
      <circle cx="38" cy="28" r="3" fill="#f9a8d4" opacity={0.3} />
      {/* Building blocks */}
      <rect x="44" y="32" width="12" height="12" rx="2" fill="url(#pr-violet)" />
      <rect x="44" y="32" width="12" height="12" rx="2" fill="url(#pr-glass-shine)" opacity={0.3} />
      <text x="50" y="42" textAnchor="middle" fontSize="8" fontWeight="800" fill="#fff" fontFamily="sans-serif">A</text>
      <rect x="38" y="44" width="12" height="12" rx="2" fill="url(#pr-felt-green)" />
      <text x="44" y="54" textAnchor="middle" fontSize="8" fontWeight="800" fill="#fff" fontFamily="sans-serif">B</text>
      <rect x="50" y="44" width="12" height="12" rx="2" fill="url(#pr-glass-amber)" />
      <text x="56" y="54" textAnchor="middle" fontSize="8" fontWeight="800" fill="#fff" fontFamily="sans-serif">C</text>
    </svg>
  ),
);
PRKidsCheckin.displayName = "PRKidsCheckin";

// ═══════════════════════════════════════════════════════════════
// GROUPS
// ═══════════════════════════════════════════════════════════════

/** Magnifying glass over people — Group Management / Find a Group */
export const PRGroupSearch = forwardRef<SVGSVGElement, IconProps>(
  (props, ref) => (
    <svg ref={ref} viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg" {...props}>
      <ellipse cx="32" cy="56" rx="20" ry="4" fill="#000" opacity={0.06} />
      {/* People silhouettes background */}
      <circle cx="20" cy="30" r="6" fill="url(#pr-violet-light)" />
      <path d="M10 48c0-5 4-8 10-8s10 3 10 8" fill="url(#pr-violet-light)" />
      <circle cx="38" cy="32" r="5" fill="url(#pr-violet-light)" opacity={0.7} />
      <path d="M30 48c0-4 3-7 8-7s8 3 8 7" fill="url(#pr-violet-light)" opacity={0.7} />
      {/* Magnifying glass */}
      <circle cx="38" cy="22" r="14" fill="none" stroke="url(#pr-violet-deep)" strokeWidth="4" />
      <circle cx="38" cy="22" r="10" fill="url(#pr-glass-blue)" opacity={0.15} />
      <circle cx="38" cy="22" r="10" fill="url(#pr-glass-shine)" opacity={0.3} />
      {/* Handle */}
      <path d="M48 32l10 10" stroke="url(#pr-warm-wood)" strokeWidth="5" strokeLinecap="round" />
      <path d="M49 33l9 9" stroke="#deb887" strokeWidth="2" strokeLinecap="round" opacity={0.5} />
    </svg>
  ),
);
PRGroupSearch.displayName = "PRGroupSearch";

/** Open book with bookmark — Curriculum */
export const PRCurriculum = forwardRef<SVGSVGElement, IconProps>(
  (props, ref) => (
    <svg ref={ref} viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg" {...props}>
      <ellipse cx="32" cy="56" rx="24" ry="4" fill="#000" opacity={0.06} />
      {/* Book stack base */}
      <rect x="8" y="44" width="48" height="6" rx="2" fill="url(#pr-violet)" opacity={0.4} />
      <rect x="10" y="40" width="44" height="6" rx="2" fill="url(#pr-violet-light)" />
      {/* Open book */}
      <path d="M6 14c0-2 1-3 3-3h20v28H9c-2 0-3-1-3-3V14z" fill="url(#pr-paper-white)" />
      <path d="M58 14c0-2-1-3-3-3H35v28h20c2 0 3-1 3-3V14z" fill="url(#pr-paper-white)" />
      <path d="M6 14c0-2 1-3 3-3h20v28H9c-2 0-3-1-3-3V14z" stroke="#d1d5db" strokeWidth="0.8" />
      <path d="M58 14c0-2-1-3-3-3H35v28h20c2 0 3-1 3-3V14z" stroke="#d1d5db" strokeWidth="0.8" />
      {/* Spine */}
      <rect x="29" y="10" width="6" height="30" fill="url(#pr-violet-deep)" />
      {/* Text lines */}
      <path d="M10 18h16M10 24h14M10 30h16" stroke="#c4b5fd" strokeWidth="1.5" strokeLinecap="round" opacity={0.4} />
      <path d="M38 18h16M38 24h14M38 30h16" stroke="#c4b5fd" strokeWidth="1.5" strokeLinecap="round" opacity={0.4} />
      {/* Bookmark */}
      <path d="M44 10v14l3-2.5 3 2.5V10" fill="url(#pr-gold-face)" />
    </svg>
  ),
);
PRCurriculum.displayName = "PRCurriculum";

/** Heart rate line / pulse — Group Health */
export const PRGroupHealth = forwardRef<SVGSVGElement, IconProps>(
  (props, ref) => (
    <svg ref={ref} viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg" {...props}>
      <ellipse cx="32" cy="56" rx="22" ry="4" fill="#000" opacity={0.06} />
      {/* Monitor screen */}
      <rect x="6" y="8" width="52" height="36" rx="5" fill="url(#pr-paper-white)" />
      <rect x="6" y="8" width="52" height="36" rx="5" stroke="#c4b5fd" strokeWidth="1.5" opacity={0.5} />
      {/* Screen inner */}
      <rect x="10" y="12" width="44" height="28" rx="3" fill="#faf5ff" />
      {/* Heartbeat line */}
      <path d="M12 30h10l3-10 4 20 4-16 3 10 4-8 3 4h11" stroke="url(#pr-violet-deep)" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" fill="none" />
      {/* Monitor stand */}
      <rect x="26" y="44" width="12" height="4" fill="url(#pr-metal)" />
      <rect x="22" y="48" width="20" height="3" rx="1.5" fill="url(#pr-metal)" />
      {/* Heart icon top-right */}
      <path d="M48 14s-1.2-1.5-2.5-.5c-1.5 1.2 0 3 2.5 4.5 2.5-1.5 4-3.3 2.5-4.5-1.3-1-2.5.5-2.5.5z" fill="#ef4444" opacity={0.7} />
    </svg>
  ),
);
PRGroupHealth.displayName = "PRGroupHealth";

/** Chat bubbles with cross — Group Messages */
export const PRGroupMessages = forwardRef<SVGSVGElement, IconProps>(
  (props, ref) => (
    <svg ref={ref} viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg" {...props}>
      <ellipse cx="32" cy="56" rx="22" ry="4" fill="#000" opacity={0.06} />
      {/* Background bubble */}
      <rect x="20" y="8" width="38" height="26" rx="6" fill="url(#pr-violet-light)" />
      <rect x="20" y="8" width="38" height="26" rx="6" stroke="#c4b5fd" strokeWidth="1" opacity={0.4} />
      <path d="M50 34l6 8-10-2" fill="url(#pr-violet-light)" />
      {/* Text lines in background bubble */}
      <path d="M28 16h22M28 22h18M28 28h14" stroke="#a78bfa" strokeWidth="2" strokeLinecap="round" opacity={0.4} />
      {/* Foreground bubble */}
      <rect x="6" y="22" width="36" height="24" rx="6" fill="url(#pr-violet)" />
      <rect x="6" y="22" width="36" height="24" rx="6" fill="url(#pr-glass-shine)" opacity={0.3} />
      <path d="M14 46l-6 8 10-2" fill="url(#pr-violet)" />
      {/* Text lines in foreground */}
      <path d="M14 30h20M14 36h16" stroke="#fff" strokeWidth="2" strokeLinecap="round" opacity={0.7} />
      {/* Small cross accent */}
      <path d="M36 26v6M33 29h6" stroke="#fde047" strokeWidth="1.5" strokeLinecap="round" opacity={0.6} />
    </svg>
  ),
);
PRGroupMessages.displayName = "PRGroupMessages";

// ═══════════════════════════════════════════════════════════════
// STAT CARD ICONS — PageHeader summaryStats
// ═══════════════════════════════════════════════════════════════

/** Calendar with upcoming arrow — Upcoming Services stat */
export const PRUpcomingServices = forwardRef<SVGSVGElement, IconProps>(
  (props, ref) => (
    <svg ref={ref} viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg" {...props}>
      <ellipse cx="32" cy="56" rx="20" ry="4" fill="#000" opacity={0.06} />
      {/* Calendar */}
      <rect x="8" y="12" width="40" height="36" rx="4" fill="url(#pr-paper-white)" />
      <rect x="8" y="12" width="40" height="10" rx="4" fill="url(#pr-violet)" />
      <rect x="8" y="18" width="40" height="4" fill="url(#pr-violet)" />
      <rect x="18" y="8" width="4" height="10" rx="2" fill="url(#pr-metal)" />
      <rect x="34" y="8" width="4" height="10" rx="2" fill="url(#pr-metal)" />
      {/* Date cells */}
      <circle cx="18" cy="32" r="3" fill="#c4b5fd" opacity={0.3} />
      <circle cx="28" cy="32" r="3" fill="#c4b5fd" opacity={0.3} />
      <circle cx="38" cy="32" r="3" fill="url(#pr-violet)" opacity={0.6} />
      <circle cx="18" cy="42" r="3" fill="#c4b5fd" opacity={0.3} />
      {/* Arrow badge */}
      <circle cx="52" cy="16" r="8" fill="url(#pr-felt-green)" />
      <circle cx="52" cy="16" r="8" fill="url(#pr-glass-shine)" />
      <path d="M49 16h6M53 13l3 3-3 3" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  ),
);
PRUpcomingServices.displayName = "PRUpcomingServices";

/** Warning triangle with exclamation — Needs Attention stat */
export const PRNeedsAttention = forwardRef<SVGSVGElement, IconProps>(
  (props, ref) => (
    <svg ref={ref} viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg" {...props}>
      <ellipse cx="32" cy="56" rx="22" ry="4" fill="#000" opacity={0.06} />
      {/* Triangle */}
      <path d="M32 8L4 52h56L32 8z" fill="url(#pr-glass-amber)" />
      <path d="M32 8L4 52h56L32 8z" fill="url(#pr-glass-shine)" opacity={0.3} />
      <path d="M32 8L4 52h56L32 8z" stroke="#b45309" strokeWidth="2" opacity={0.4} />
      {/* Inner lighter triangle */}
      <path d="M32 18L12 48h40L32 18z" fill="#fef3c7" opacity={0.3} />
      {/* Exclamation */}
      <rect x="29" y="24" width="6" height="14" rx="3" fill="#92400e" />
      <circle cx="32" cy="44" r="3" fill="#92400e" />
    </svg>
  ),
);
PRNeedsAttention.displayName = "PRNeedsAttention";

/** People group with count badge — Active Volunteers stat */
export const PRActiveVolunteers = forwardRef<SVGSVGElement, IconProps>(
  (props, ref) => (
    <svg ref={ref} viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg" {...props}>
      <ellipse cx="32" cy="56" rx="22" ry="4" fill="#000" opacity={0.06} />
      {/* Person 1 (center front) */}
      <circle cx="28" cy="22" r="8" fill="url(#pr-violet-light)" />
      <circle cx="28" cy="22" r="8" stroke="#a78bfa" strokeWidth="1" opacity={0.5} />
      <path d="M14 48c0-6 6-10 14-10s14 4 14 10" fill="url(#pr-violet-light)" />
      <path d="M14 48c0-6 6-10 14-10s14 4 14 10" stroke="#a78bfa" strokeWidth="1" opacity={0.4} />
      {/* Person 2 (right back) */}
      <circle cx="44" cy="24" r="6" fill="url(#pr-violet)" opacity={0.5} />
      <path d="M36 48c0-4 3-7 8-7s8 3 8 7" fill="url(#pr-violet)" opacity={0.4} />
      {/* Person 3 (left back) */}
      <circle cx="12" cy="26" r="5" fill="url(#pr-violet)" opacity={0.4} />
      <path d="M4 48c0-3 3-6 8-6s8 3 8 6" fill="url(#pr-violet)" opacity={0.3} />
      {/* Count badge */}
      <circle cx="52" cy="14" r="8" fill="url(#pr-felt-green)" />
      <circle cx="52" cy="14" r="8" fill="url(#pr-glass-shine)" />
      <text x="52" y="18" textAnchor="middle" fontSize="10" fontWeight="800" fill="#fff" fontFamily="sans-serif">#</text>
    </svg>
  ),
);
PRActiveVolunteers.displayName = "PRActiveVolunteers";

/** Church with people — Active Ministries stat */
export const PRActiveMinistries = forwardRef<SVGSVGElement, IconProps>(
  (props, ref) => (
    <svg ref={ref} viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg" {...props}>
      <ellipse cx="32" cy="56" rx="22" ry="4" fill="#000" opacity={0.06} />
      {/* Church silhouette */}
      <rect x="14" y="28" width="36" height="20" rx="2" fill="url(#pr-violet-light)" />
      <path d="M18 28l14-12 14 12" fill="url(#pr-violet)" opacity={0.7} />
      <path d="M18 28l14-12 14 12" fill="url(#pr-glass-shine)" opacity={0.3} />
      {/* Cross */}
      <path d="M32 10v8M29 13h6" stroke="#fde047" strokeWidth="2" strokeLinecap="round" />
      {/* Window */}
      <circle cx="32" cy="32" r="4" fill="url(#pr-stained-warm)" opacity={0.7} />
      {/* Door */}
      <rect x="28" y="40" width="8" height="8" rx="1" fill="url(#pr-warm-wood)" />
      {/* People silhouettes at bottom */}
      <circle cx="16" cy="42" r="3" fill="url(#pr-violet)" opacity={0.4} />
      <circle cx="48" cy="42" r="3" fill="url(#pr-violet)" opacity={0.4} />
      <circle cx="10" cy="44" r="2.5" fill="url(#pr-violet)" opacity={0.3} />
      <circle cx="54" cy="44" r="2.5" fill="url(#pr-violet)" opacity={0.3} />
      {/* Violet badge with count */}
      <circle cx="52" cy="14" r="8" fill="url(#pr-violet-deep)" />
      <circle cx="52" cy="14" r="8" fill="url(#pr-glass-shine)" />
      <text x="52" y="18" textAnchor="middle" fontSize="10" fontWeight="800" fill="#fff" fontFamily="sans-serif">#</text>
    </svg>
  ),
);
PRActiveMinistries.displayName = "PRActiveMinistries";
